import { Link } from "@mui/material";

import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { faqs, howItWorks } from "./helpers";
import { vendorUUIDs } from "../../constants";

import StaticSeatMap from "public/images/newhampshirefishercats/FisherCatsMap.png";
import FindGamesBanner from "public/images/newhampshirefishercats/Buy More Save More Feb Update.png";

import type { TeamConfig } from "../types";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/NewHampshireFisherCats.png";

const config: TeamConfig = {
  teamSchema: "newhampshirefishercats_flex",
  browserTitle: "Buy More Save More",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "New Hampshire Fisher Cats",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        discount: 10,
        customProgressBarMessage:
          "Congrats! You have unlocked 10% off your tickets! Add 3 more games for 15% off, 5+ games for 20% off & a sweepstakes entry for batting practice, or checkout now.",
      },
      {
        id: 2,
        minSelectedAmount: 3,
        updatedAccountGroup: 0,
        discount: 10,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 2 more games for 15% off your tickets or 4+ games for 20% off & a sweepstakes entry for batting practice at Delta Dental Stadium.",
      },
      {
        id: 3,
        minSelectedAmount: 4,
        updatedAccountGroup: 0,
        discount: 10,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 15% off your tickets or 3+ games for 20% off & a sweepstakes entry for batting practice at Delta Dental Stadium.",
      },
      {
        id: 4,
        minSelectedAmount: 5,
        updatedAccountGroup: 1,
        discount: 15,
        customProgressBarMessage:
          "Congrats! You have unlocked 15% off your tickets! Add 2+ games for 20% off & a sweepstakes entry for batting practice at Delta Dental Stadium or checkout now.",
      },
      {
        id: 5,
        minSelectedAmount: 6,
        updatedAccountGroup: 1,
        discount: 15,
        hideOfferFromModal: true,
        customProgressBarMessage:
          "Add 1 more game for 20% off your tickets & a sweepstakes entry for batting practice at Delta Dental Stadium.",
      },
      {
        id: 6,
        minSelectedAmount: 7,
        updatedAccountGroup: 2,
        discount: 20,
        customProgressBarMessage:
          "Congrats! You have unlocked 20% off your tickets & a sweepstakes entry for batting practice at Delta Dental Stadium! Add more games or checkout now.",
        customAditionalMessage:
          " for 20% off & a sweepstakes entry for batting practice at Delta Dental Stadium?",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "40%",
      display: "block",
      marginX: "auto",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 10,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isPerksEnabled: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=NHCM_MYTIXX&orgid=57690#/auth/login?fullret=%2Fticketmanagement%2F%3Fagency%3DNHCM_MYTIXX%26orgid%3D57690",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    authBackgroundImage: "",
    findGamesBannerImage: FindGamesBanner.src,
    checkoutStatusPageImage: "",
    logoImage: LogoImage + "?width=128&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: false,
      hideAccessibilityFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          href="tel:6036412005"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (603) 641-2005
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#c6021e",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    purchaseSeats: {
      similarSeatModalBody: (seatTitle: string) => (
        <>
          We are going to apply the same or similar seats to all games in your
          pack. Seat locations will be visible on the checkout page.
        </>
      ),

      progressBarText:
        "Add 2 games and get 10% off, add 5 games and get 15% off or add 7+ games for 20% off your tickets and sweepstakes entry to take batting practice at the Delta Dental Stadium!",
      progressBarAditionalText: " to continue!",
    },

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Your New Hampshire Fisher Cats tickets are confirmed! Your tickets will be available within 24 hours.",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
